/**
 * scheduleExpression — pure helpers to convert between cron strings
 * (persisted in `roros_cadence_schedules.schedule_expression`) and
 * structured forms consumed by the cadence pickers in Structure Settings.
 *
 * The server's cadence evaluator does NOT parse these cron strings
 * (it buckets by interval days — see cadence-evaluator.ts), so the cron
 * format here is effectively display metadata with a human-ish shape
 * that matches the Launchpad seeds.
 *
 * Fields of a 5-part cron string: `minute hour day-of-month month day-of-week`
 *   - day-of-week: 0 = Sunday ... 6 = Saturday (classic cron)
 *
 * This module is a thin, pure helper — no JSX, no side effects.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export type CadenceScheduleType =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'trimester'
  | 'annual';

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type Ordinal = 'first' | 'second' | 'third' | 'fourth' | 'last';

export type ParsedSchedule =
  | { kind: 'daily'; weekdays: Weekday[] }
  | { kind: 'weekly'; weekday: Weekday }
  | { kind: 'monthly'; ordinal: Ordinal; weekday: Weekday }
  | { kind: 'quarterly'; ordinal: Ordinal; weekday: Weekday | null }
  | { kind: 'trimester'; ordinal: Ordinal; weekday: Weekday | null }
  | { kind: 'annual'; ordinal: Ordinal; weekday: Weekday };

// ─── Fixed months per cadence type (NOT editable — defines the cadence) ─────

/** Months in which the cadence can fire. `null` = any month. */
const FIXED_MONTHS: Record<CadenceScheduleType, string | null> = {
  daily: null,
  weekly: null,
  monthly: null,
  quarterly: '3,6,9,12',
  trimester: '4,8,12',
  annual: '12',
};

// ─── Ordinal ↔ day-of-month range ──────────────────────────────────────────

const ORDINAL_TO_DOM: Record<Ordinal, string> = {
  first: '1-7',
  second: '8-14',
  third: '15-21',
  fourth: '22-28',
  last: '25-31',
};

/** Trimester uses 25-30 as "last" in the seed (Apr/Aug are 30-day months). */
const TRIMESTER_LAST_DOM = '25-30';

function domToOrdinal(dom: string): Ordinal | null {
  switch (dom) {
    case '1-7':
      return 'first';
    case '8-14':
      return 'second';
    case '15-21':
      return 'third';
    case '22-28':
      return 'fourth';
    case '25-31':
    case '25-30':
      return 'last';
    default:
      return null;
  }
}

// ─── Seed defaults (mirror of server/modules/launchpad/launchpad.types.ts) ─

/**
 * Default structured schedule per cadence type — mirrors the Launchpad seed
 * crons in `CADENCE_SCHEDULE_SEED`. Used to populate `CadenceScheduleData.schedule`
 * when the backend hasn't yet returned a row (e.g. Launchpad initial state).
 */
export function defaultScheduleFor(type: CadenceScheduleType): ParsedSchedule {
  switch (type) {
    case 'daily':
      return { kind: 'daily', weekdays: [1, 2, 3, 4, 5] };
    case 'weekly':
      return { kind: 'weekly', weekday: 1 };
    case 'monthly':
      return { kind: 'monthly', ordinal: 'first', weekday: 1 };
    case 'quarterly':
      return { kind: 'quarterly', ordinal: 'last', weekday: null };
    case 'trimester':
      return { kind: 'trimester', ordinal: 'last', weekday: null };
    case 'annual':
      return { kind: 'annual', ordinal: 'first', weekday: 1 };
  }
}

// ─── Time helpers ──────────────────────────────────────────────────────────

/**
 * Extract `"HH:MM"` (24h, zero-padded) from a cron string.
 * Falls back to `"09:00"` if the cron is malformed.
 */
export function parseTimeFromCron(expr: string): string {
  const parts = (expr ?? '').trim().split(/\s+/);
  if (parts.length < 5) return '09:00';
  const minute = Number(parts[0]);
  const hour = Number(parts[1]);
  if (!Number.isInteger(minute) || !Number.isInteger(hour)) return '09:00';
  if (minute < 0 || minute > 59 || hour < 0 || hour > 23) return '09:00';
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Rewrite the minute + hour fields of a cron string, preserving the
 * day-of-month / month / day-of-week fields verbatim.
 */
export function withTimeInCron(expr: string, time: string): string {
  const [hhRaw, mmRaw] = (time ?? '09:00').split(':');
  const hh = clampInt(Number(hhRaw), 0, 23, 9);
  const mm = clampInt(Number(mmRaw), 0, 59, 0);
  const parts = (expr ?? '').trim().split(/\s+/);
  if (parts.length < 5) return `${mm} ${hh} * * *`;
  return [String(mm), String(hh), parts[2], parts[3], parts[4]].join(' ');
}

function clampInt(n: number, min: number, max: number, fallback: number): number {
  if (!Number.isInteger(n)) return fallback;
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

// ─── Weekday parsing helpers ───────────────────────────────────────────────

function parseWeekdayField(field: string): Weekday[] {
  const trimmed = field.trim();
  if (trimmed === '*') return [0, 1, 2, 3, 4, 5, 6];

  const out = new Set<Weekday>();
  for (const segment of trimmed.split(',')) {
    if (segment.includes('-')) {
      const [from, to] = segment.split('-').map((s) => Number(s));
      if (!Number.isInteger(from) || !Number.isInteger(to)) continue;
      for (let d = from; d <= to; d++) {
        if (isWeekday(d)) out.add(d);
      }
    } else {
      const d = Number(segment);
      if (isWeekday(d)) out.add(d);
    }
  }
  return [...out].sort((a, b) => a - b) as Weekday[];
}

function isWeekday(n: number): n is Weekday {
  return Number.isInteger(n) && n >= 0 && n <= 6;
}

function parseSingleWeekday(field: string): Weekday | null {
  const trimmed = field.trim();
  if (trimmed === '*') return null;
  const n = Number(trimmed);
  return isWeekday(n) ? n : null;
}

/** Collapse a sorted weekday list into a cron-friendly field. */
function serializeWeekdayList(list: Weekday[]): string {
  if (list.length === 7) return '*';
  if (list.length === 0) return '*';
  const sorted = [...list].sort((a, b) => a - b);
  // Detect Mon-Fri (1-5) → emit the canonical range shorthand to match seeds.
  if (sorted.length === 5 && sorted.join(',') === '1,2,3,4,5') return '1-5';
  return sorted.join(',');
}

// ─── Schedule parse / serialize ────────────────────────────────────────────

/**
 * Parse a cron string into a structured {@link ParsedSchedule} for the given
 * cadence type. Returns a best-effort fallback (first Monday / Monday) if the
 * cron can't be decoded — callers can display it and the user will overwrite
 * on next edit.
 */
export function parseSchedule(type: CadenceScheduleType, expr: string): ParsedSchedule {
  const parts = (expr ?? '').trim().split(/\s+/);
  // Normalise to 5 parts so we can still produce a fallback for malformed input.
  while (parts.length < 5) parts.push('*');
  const dom = parts[2];
  const dow = parts[4];

  switch (type) {
    case 'daily':
      return { kind: 'daily', weekdays: parseWeekdayField(dow) };

    case 'weekly': {
      const wd = parseSingleWeekday(dow);
      return { kind: 'weekly', weekday: wd ?? 1 };
    }

    case 'monthly': {
      const ordinal = domToOrdinal(dom) ?? 'first';
      const wd = parseSingleWeekday(dow);
      return { kind: 'monthly', ordinal, weekday: wd ?? 1 };
    }

    case 'quarterly': {
      const ordinal = domToOrdinal(dom) ?? 'last';
      const wd = parseSingleWeekday(dow);
      return { kind: 'quarterly', ordinal, weekday: wd };
    }

    case 'trimester': {
      const ordinal = domToOrdinal(dom) ?? 'last';
      const wd = parseSingleWeekday(dow);
      return { kind: 'trimester', ordinal, weekday: wd };
    }

    case 'annual': {
      const ordinal = domToOrdinal(dom) ?? 'first';
      const wd = parseSingleWeekday(dow);
      return { kind: 'annual', ordinal, weekday: wd ?? 1 };
    }
  }
}

/**
 * Serialize a {@link ParsedSchedule} plus a `"HH:MM"` time into a cron string.
 * Fixed months per cadence type (quarterly → Mar/Jun/Sep/Dec, etc.) are baked
 * in — they are defined by the cadence semantics and not user-editable.
 */
export function serializeSchedule(
  type: CadenceScheduleType,
  parsed: ParsedSchedule,
  time: string,
): string {
  const [hhRaw, mmRaw] = (time ?? '09:00').split(':');
  const hh = clampInt(Number(hhRaw), 0, 23, 9);
  const mm = clampInt(Number(mmRaw), 0, 59, 0);
  const month = FIXED_MONTHS[type] ?? '*';

  switch (type) {
    case 'daily': {
      if (parsed.kind !== 'daily') throw new Error('schedule kind mismatch');
      const dow = serializeWeekdayList(parsed.weekdays);
      return `${mm} ${hh} * * ${dow}`;
    }

    case 'weekly': {
      if (parsed.kind !== 'weekly') throw new Error('schedule kind mismatch');
      return `${mm} ${hh} * * ${parsed.weekday}`;
    }

    case 'monthly': {
      if (parsed.kind !== 'monthly') throw new Error('schedule kind mismatch');
      const dom = ORDINAL_TO_DOM[parsed.ordinal];
      return `${mm} ${hh} ${dom} * ${parsed.weekday}`;
    }

    case 'quarterly':
    case 'trimester': {
      if (parsed.kind !== type) throw new Error('schedule kind mismatch');
      const dom =
        parsed.ordinal === 'last' && type === 'trimester'
          ? TRIMESTER_LAST_DOM
          : ORDINAL_TO_DOM[parsed.ordinal];
      const dow = parsed.weekday === null ? '*' : String(parsed.weekday);
      return `${mm} ${hh} ${dom} ${month} ${dow}`;
    }

    case 'annual': {
      if (parsed.kind !== 'annual') throw new Error('schedule kind mismatch');
      const dom = ORDINAL_TO_DOM[parsed.ordinal];
      return `${mm} ${hh} ${dom} ${month} ${parsed.weekday}`;
    }
  }
}
